<template>
  <div class="toolbar">
    <b-navbar
      class="p-1"
      toggleable="md"
      type="light"
      variant="white">
      <b-container>
        <b-navbar-brand
          to="/">
          <img
            class="brand-logo"
            src="/images/brand/zoop-wordmark.png"
            alt="Logo">
        </b-navbar-brand>

        <b-navbar-nav class="d-block">
          <b-nav-item-dropdown>
            <template v-slot:button-content>
              <b-icon-cursor-fill/>
              <span class="d-none d-sm-inline">&nbsp;{{ currentRegion.name }}</span>
            </template>
            <b-dropdown-item
              v-for="region in pickerRegions"
              :key="region.slug"
              @click="selectRegion(region)">
              {{ region.name }}
              <span
                v-if="region.slug === currentRegion.slug"
                class="text-muted ml-1">
                (current)
              </span>
            </b-dropdown-item>
            <b-dropdown-divider/>
            <b-dropdown-item to="/locations">
              About our ZoopLocal Areas...
             </b-dropdown-item>
          </b-nav-item-dropdown>
        </b-navbar-nav>

        <!-- Right aligned nav items -->
        <b-navbar-nav class="ml-auto d-lg-flex flex-row">
          <b-button
            size="sm"
            variant="outline-dark"
            class="mx-3 d-none d-lg-block"
            squared
            @click="$router.push('/account/listings/new')">
            Sell on ZoopLocal
          </b-button>
          <!-- Desktop-specific -->
          <b-nav-item-dropdown
            v-if="isAuthenticated"
            class="px-1 d-none d-md-block"
            right>
            <template v-slot:button-content>
              <span class="text-dark">
                <strong>{{ user.firstName }} {{ user.lastName }}</strong>
              </span>
            </template>
            <b-dropdown-item
              href="#"
              to="/account/listings/">
              My Listings
            </b-dropdown-item>
            <b-dropdown-item
              href="#"
              to="/account/profile">
              Account
            </b-dropdown-item>
            <b-dropdown-divider/>
            <span v-if="isAdmin">
              <b-dropdown-item
                href="#"
                to="/manage">
                Management
              </b-dropdown-item>
              <b-dropdown-divider/>
            </span>
            <b-dropdown-item
              href="#"
              @click="logOut">
              Sign Out
             </b-dropdown-item>
          </b-nav-item-dropdown>
          <b-nav-item
            v-if="isAuthenticated"
            class="d-md-none px-1 mr-2"
            to="/account">
            <span class="text-dark">
              <strong>{{user.name}}</strong>
            </span>
          </b-nav-item>
          <!-- Mobile-only -->
          <b-nav-item
            v-if="isAuthenticated"
            class="d-md-none mr-2"
            to="/account/listings/">
            <span
              class="mr-2">
              <font-awesome-icon
                :icon="['far', 'address-card']"/>
            </span>
          </b-nav-item>
          <b-nav-item
            v-if="isAuthenticated"
            class="mr-2"
            to="/account/favorites">
            <span
              class="mr-2 with-badge"
              :data-count="favoritesCount ? favoritesCount : null">
              <font-awesome-icon
                :icon="['far', 'star']"/>
            </span>
          </b-nav-item>
          <b-nav-item
            v-if="isAuthenticated"
            class="mr-2"
            to="/account/purchases">
            <span
              :data-count="unreadCount ? unreadCount : null"
              class="with-badge">
            <font-awesome-icon
              :icon="['far', 'comments']"/>
            </span>
          </b-nav-item>
          <!-- Mobile-specific sign out -->
          <b-nav-item
            v-if="isAuthenticated"
            class="ml-2 d-md-none"
            @click="logOut()">
            <span class="text-dark">
              Sign Out
            </span>
          </b-nav-item>
          <b-nav-item
            v-if="!isAuthenticated"
            class="ml-2"
            to="/login">
            <span class="text-dark">
              Log In
            </span>
          </b-nav-item>
          <b-nav-item
            v-if="!isAuthenticated"
            class="ml-2"
            to="/authentication/register">
            <span class="text-dark">
              Sign Up
            </span>
          </b-nav-item>
        </b-navbar-nav>
      </b-container>
    </b-navbar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'Toolbar',
  data() {
    return {}
  },
  methods: {
    async logOut() {
      await this.$store.dispatch('Authentication/logOut')
      this.$router.push('/')
      this.$bvToast.toast('You were successfully logged out of your ZoopLocal account', {
        title: 'Logged Out',
        autoHideDelay: 3000,
      })
    },
    selectRegion(region) {
      this.$store.dispatch('Region/setRegion', region.slug)
      // Only swap the URL if the user is on the region-scoped listings page;
      // otherwise stay where they are (home, account, etc.) — those views
      // reactively re-render from the store.
      if (this.$route.name === 'listings' && this.$route.params.regionSlug !== region.slug) {
        this.$router.push(`/listings/${region.slug}`)
      }
    },
  },
  computed: {
    ...mapGetters('Authentication', [
      'isAuthenticated',
      'isAdmin',
      'user',
    ]),
    ...mapGetters('Favorites', [
      'favoritesCount',
    ]),
    ...mapGetters('Messages', [
      'unreadCount',
    ]),
    ...mapGetters('Region', {
      currentRegion: 'current',
      regionsAvailable: 'available',
    }),
    // Dedupe by slug so the dropdown can never be missing one of the known
    // regions even if the store's `available` was overwritten mid-session.
    // API entries (later in the spread) override the hardcoded placeholders
    // for the same slug, so the canonical id/name win.
    pickerRegions() {
      const bySlug = new Map()
      const items = [
        { slug: 'south-walton', name: 'South Walton' },
        { slug: 'memphis', name: 'Memphis' },
        ...(this.regionsAvailable || []),
      ]
      items.forEach(r => bySlug.set(r.slug, r))
      return Array.from(bySlug.values())
    },
  },
}
</script>

<style lang="scss">
.brand-logo {
  height: 40px;
}
.toolbar {
  border-bottom: 1px solid #E9E9E9;
  padding: 4px 10px;

  // bootstrap-vue disables Popper for dropdowns inside a navbar and relies on
  // Bootstrap's CSS, which positions the menu `static` (inline, reflowing the
  // bar) below the navbar's expand breakpoint. This navbar has no hamburger to
  // collapse into, so force the menu to float at all widths.
  .navbar-nav .dropdown-menu {
    position: absolute;
    float: none;

    // Let long items (e.g. the "About our…" link) wrap so the menu keeps to its
    // min-width instead of growing wide enough to run off a narrow viewport.
    .dropdown-item {
      white-space: normal;
    }
  }
}
</style>
